import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { MainLeftTitle } from "../../../_uiComponents/WrapperComponents";
import LcvPrizePotSummary from "./views/LcvPrizePotSummary";
import LcvPrizePotGrid from "./views/LcvPrizePotGrid";
import LcvPrizePotGridSkeleton from "./views/LcvPrizePotGridSkeleton";
import LcvPrizeQuantityIncreaseDialog from "./views/LcvPrizeQuantityIncreaseDialog";
import LcvIncreaseBudgetDialog from "./views/LcvIncreaseBudgetDialog";
import LcvBudgetActivityGrid from "./views/LcvBudgetActivityGrid";
import LcvCampaignSelect from "../shared/LcvCampaignSelect";
import useLcvCampaign from "../shared/useLcvCampaign";

import { lcvPrizePotConstants } from "../../../../_redux/_constants/lcvPrizePot.constants";
import {
     lcvPrizePotGetRequest,
     lcvPrizePotSetAvailabilityRequest,
     lcvPrizePotIncreaseQuantityRequest,
     lcvPrizePotClearActionResult,
     lcvPrizePotGetBudgetActivityRequest,
     lcvPrizePotAddBudgetEntryRequest,
} from "../../../../_redux/_actions/lcvPrizePot.action";

// The campaign prize pot, its controls, and the history of the money behind it.
//
// Adding stock asks for a quantity and nothing else. If it would take the pot past the authorised
// budget the backend refuses and says so, and raising the budget is a separate deliberate act. That
// keeps one decision per action: this screen spends, the budget history authorises.
//
// Every figure, warning and outcome shown here comes from the response. The backend owns the wording
// because it owns the rule behind it.

const WRITE_REQUESTS = [
     lcvPrizePotConstants.LCV_PRIZE_POT_SET_AVAILABILITY_REQUEST,
     lcvPrizePotConstants.LCV_PRIZE_POT_INCREASE_QUANTITY_REQUEST,
     lcvPrizePotConstants.LCV_PRIZE_POT_ADD_BUDGET_ENTRY_REQUEST,
];

export default function LcvPrizePotWrapper() {
     const actionDispatch = useDispatch();

     const prizePot = useSelector((state) => state?.lcvPrizePot?.prizePot);
     const actionResult = useSelector((state) => state?.lcvPrizePot?.actionResult);
     const budgetActivity = useSelector((state) => state?.lcvPrizePot?.budgetActivity);

     const pendingRequests = useSelector((state) => state?.loading?.pendingRequests || []);

     const isLoading = pendingRequests.includes(lcvPrizePotConstants.LCV_PRIZE_POT_GET_REQUEST);
     const isBusy = WRITE_REQUESTS.some((request) => pendingRequests.includes(request));

     const { selectedCampaignId } = useLcvCampaign();

     const [prizeBeingIncreased, setPrizeBeingIncreased] = useState(null);
     const [isIncreasingBudget, setIsIncreasingBudget] = useState(false);

     useEffect(() => {
          if (!selectedCampaignId) {
               return undefined;
          }

          actionDispatch(lcvPrizePotGetRequest({}));
          actionDispatch(lcvPrizePotGetBudgetActivityRequest({}));

          return () => {
               actionDispatch(lcvPrizePotClearActionResult());
          };
     }, [actionDispatch, selectedCampaignId]);

     const changeAvailability = useCallback(
          (prize, isAvailableToWin) => {
               actionDispatch(
                    lcvPrizePotSetAvailabilityRequest({
                         prizePotItemId: prize.id,
                         isAvailableToWin,
                    }),
               );
          },
          [actionDispatch],
     );

     const confirmIncrease = useCallback(
          (payload) => {
               actionDispatch(lcvPrizePotIncreaseQuantityRequest(payload));
               setPrizeBeingIncreased(null);
          },
          [actionDispatch],
     );

     const confirmBudgetIncrease = useCallback(
          (payload) => {
               actionDispatch(lcvPrizePotAddBudgetEntryRequest(payload));
               setIsIncreasingBudget(false);
          },
          [actionDispatch],
     );

     const outcome = typeof actionResult === "string" ? actionResult : actionResult?.message;

     return (
          <>
               <MainLeftTitle title="LCV Prize Pot" />

               <div className="sba-telerik-components-wr">
                    <LcvCampaignSelect />

                    {isLoading && !prizePot ? (
                         <LcvPrizePotGridSkeleton />
                    ) : (
                         <Box>
                              {outcome && (
                                   <Alert severity="success" sx={{ mb: 2 }}>
                                        {outcome}
                                   </Alert>
                              )}

                              {(prizePot?.warnings || []).map((warning) => (
                                   <Alert key={warning} severity="warning" sx={{ mb: 2 }}>
                                        {warning}
                                   </Alert>
                              ))}

                              <LcvPrizePotSummary
                                   prizePot={prizePot}
                                   authorisedGbp={budgetActivity?.authorisedGbp}
                              />

                              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                                   <Button
                                        type="button"
                                        variant="outlined"
                                        disabled={isBusy}
                                        onClick={() => setIsIncreasingBudget(true)}
                                   >
                                        Increase budget
                                   </Button>
                              </Box>

                              <LcvPrizePotGrid
                                   prizes={prizePot?.prizes || []}
                                   isBusy={isBusy}
                                   onAvailabilityChange={changeAvailability}
                                   onIncreaseQuantity={setPrizeBeingIncreased}
                              />

                              <LcvBudgetActivityGrid entries={budgetActivity?.entries || []} />
                         </Box>
                    )}

                    {prizeBeingIncreased && (
                         <LcvPrizeQuantityIncreaseDialog
                              prize={prizeBeingIncreased}
                              isBusy={isBusy}
                              onCancel={() => setPrizeBeingIncreased(null)}
                              onConfirm={confirmIncrease}
                         />
                    )}

                    {isIncreasingBudget && (
                         <LcvIncreaseBudgetDialog
                              authorisedGbp={budgetActivity?.authorisedGbp}
                              isBusy={isBusy}
                              onCancel={() => setIsIncreasingBudget(false)}
                              onConfirm={confirmBudgetIncrease}
                         />
                    )}
               </div>
          </>
     );
}
